import type { JSX } from 'react'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Copy, ExternalLink } from 'lucide-react'
import { wsRoom } from '@apiary/shared'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { formatCountdown, formatDeviceCode } from '@/lib/format'
import { useWsRoom } from '@/hooks/useWsRoom'
import { useSessionStore } from '@/stores/session'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Dialog } from '@/ui/primitives/Dialog'
import { StatusDot, type StatusTone } from '@/ui/primitives/StatusDot'

/**
 * Вход Microsoft по коду устройства.
 *
 * Пароль Microsoft панель не спрашивает и не хранит — пользователь вводит его на
 * стороне Microsoft, а сюда приходит только результат. Статус меняется по WS без
 * перезагрузки: редьюсер пишет `mcAccount.authCode` / `mcAccount.status` в кэш по
 * ключу `qk.mcAccounts.authStatus(id)`, а REST `auth-status` нужен для первого
 * рендера, пока событие ещё не пришло.
 *
 * Код набран крупно и монопространственно: его переписывают глазами в другое
 * окно, и «0 против O» здесь стоит дороже красоты.
 */

export interface DeviceCodeDialogProps {
  open: boolean
  accountId: string | null
  onClose: () => void
  /** Запросить новый код, когда прежний истёк. */
  onRetry: () => void
  retrying?: boolean
}

export function DeviceCodeDialog({
  open,
  accountId,
  onClose,
  onRetry,
  retrying = false
}: DeviceCodeDialogProps): JSX.Element {
  const orgId = useSessionStore(state => state.activeOrgId)
  // События `mcAccount.*` летят в комнату организации.
  useWsRoom(open && orgId !== null ? wsRoom.org(orgId) : null)

  const status = useQuery({
    queryKey: qk.mcAccounts.authStatus(accountId ?? 'none'),
    queryFn: () => api.mcAccounts.authStatus(accountId as string),
    enabled: open && accountId !== null
  })

  const data = status.data
  const [copied, setCopied] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  // Обратный отсчёт — единственная живая цифра в диалоге, поэтому таймер здесь.
  useEffect(() => {
    if (!open) return
    const timer = globalThis.setInterval(() => setNow(Date.now()), 1000)
    return () => globalThis.clearInterval(timer)
  }, [open])

  useEffect(() => {
    if (!copied) return
    const timer = globalThis.setTimeout(() => setCopied(false), 2000)
    return () => globalThis.clearTimeout(timer)
  }, [copied])

  const countdown = formatCountdown(data?.expiresAt, now)
  const expired = data?.expiresAt !== undefined && countdown === '00:00'
  const verificationUri = data?.verificationUri ?? 'https://microsoft.com/link'
  const code = data?.userCode ?? null
  const canRetry = expired || data?.status === 'AUTH_FAILED' || data?.status === 'EXPIRED'

  const line = ((): { text: string; tone: StatusTone; pulse: boolean } => {
    if (data === undefined) return { text: 'Готовим код входа…', tone: 'busy', pulse: true }
    if (data.status === 'READY') return { text: 'Аккаунт подключён', tone: 'live', pulse: false }
    if (data.status === 'AUTH_FAILED') {
      return {
        text: `Вход не удался: ${data.error ?? 'причина неизвестна'}`,
        tone: 'fail',
        pulse: false
      }
    }
    if (data.status === 'EXPIRED' || expired) {
      return { text: 'Код истёк — запросите новый', tone: 'fail', pulse: false }
    }
    if (data.status === 'AUTH_PENDING') {
      return { text: 'Ждём подтверждения в браузере', tone: 'busy', pulse: true }
    }
    return { text: 'Готовим код входа…', tone: 'busy', pulse: true }
  })()

  const copy = async (): Promise<void> => {
    if (code === null) return
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
    } catch {
      // Буфер может быть недоступен без разрешения — код всё равно виден глазами.
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Вход через Microsoft"
      note="Пароль вводится на сайте Microsoft — панель его не видит"
      footer={
        <>
          {canRetry ? (
            <Button variant="secondary" loading={retrying} onClick={onRetry}>
              Запросить новый код
            </Button>
          ) : null}
          <Button variant="primary" onClick={onClose}>
            Готово
          </Button>
        </>
      }
    >
      <ol className="flex flex-col gap-5">
        <li className="flex flex-col gap-2">
          <p className="text-base text-ink">1. Откройте страницу входа Microsoft</p>
          <a
            href={verificationUri}
            target="_blank"
            rel="noreferrer noopener"
            className="press inline-flex w-fit items-center gap-2 rounded-sm border border-line bg-panel-2 px-3 py-2 text-base text-ink hover:border-line-strong hover:bg-panel-3"
          >
            <ExternalLink aria-hidden className="size-4" />
            {verificationUri}
          </a>
        </li>

        <li className="flex flex-col gap-2">
          <p className="text-base text-ink">2. Введите этот код</p>
          <div className="flex items-center gap-2">
            <output
              aria-label={code === null ? 'Код ещё не выдан' : `Код входа ${code}`}
              className="flex-1 rounded-sm border border-line bg-panel-2 px-4 py-3 text-center font-mono text-xl font-medium tracking-wide text-ink"
            >
              {code === null ? '········' : formatDeviceCode(code)}
            </output>
            <IconButton
              label="Скопировать код"
              disabled={code === null}
              onClick={() => void copy()}
            >
              <Copy aria-hidden className="size-4" />
            </IconButton>
          </div>
          <div className="flex min-h-5 items-baseline justify-between gap-2 text-2xs">
            <span role="status" className="text-live">
              {copied ? 'Скопировано' : ''}
            </span>
            {countdown === null ? null : (
              <span className="font-mono tabular-nums text-ink-muted">
                код действует ещё {countdown}
              </span>
            )}
          </div>
        </li>
      </ol>

      <p
        role="status"
        aria-live="polite"
        className="mt-4 flex items-center gap-2 border-t border-line pt-4 text-base text-ink"
      >
        <StatusDot tone={line.tone} pulse={line.pulse} />
        {line.text}
      </p>
    </Dialog>
  )
}
